import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface VideoCallState {
  callId: string | null;
  isIncoming: boolean;
  isCalling: boolean;
  isConnected: boolean;
  isReconnecting: boolean;
  isScreenSharing: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
}

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export const useVideoCall = (chatId: string, otherUserId: string) => {
  const { user } = useAuthContext();
  const [callState, setCallState] = useState<VideoCallState>({
    callId: null,
    isIncoming: false,
    isCalling: false,
    isConnected: false,
    isReconnecting: false,
    isScreenSharing: false,
    localStream: null,
    remoteStream: null,
  });

  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Initialize signaling channel
  useEffect(() => {
    if (!chatId || !user) return;

    const channel = supabase.channel(`video-call:${chatId}`, {
      config: { broadcast: { self: false } },
    });

    channel
      .on("broadcast", { event: "offer" }, async ({ payload }) => {
        console.log("Received offer:", payload);
        if (payload.targetId === user.id) {
          setCallState((prev) => ({
            ...prev,
            callId: payload.callId,
            isIncoming: true,
          }));
        }
      })
      .on("broadcast", { event: "answer" }, async ({ payload }) => {
        console.log("Received answer:", payload);
        if (payload.targetId === user.id && peerConnectionRef.current) {
          await peerConnectionRef.current.setRemoteDescription(
            new RTCSessionDescription(payload.answer)
          );
        }
      })
      .on("broadcast", { event: "ice-candidate" }, async ({ payload }) => {
        console.log("Received ICE candidate:", payload);
        if (payload.targetId === user.id && peerConnectionRef.current) {
          await peerConnectionRef.current.addIceCandidate(
            new RTCIceCandidate(payload.candidate)
          );
        }
      })
      .on("broadcast", { event: "end-call" }, ({ payload }) => {
        if (payload.targetId === user.id) {
          endCall();
        }
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId, user]);

  // Listen for incoming calls from database
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`incoming-calls-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "video_calls",
          filter: `callee_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new.status === "ringing") {
            setCallState((prev) => ({
              ...prev,
              callId: payload.new.id,
              isIncoming: true,
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const setupPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && channelRef.current) {
        channelRef.current.send({
          type: "broadcast",
          event: "ice-candidate",
          payload: {
            candidate: event.candidate,
            targetId: otherUserId,
          },
        });
      }
    };

    pc.ontrack = (event) => {
      console.log("Received remote track:", event.streams[0]);
      setCallState((prev) => ({
        ...prev,
        remoteStream: event.streams[0],
        isConnected: true,
      }));
    };

    pc.onconnectionstatechange = () => {
      console.log("Connection state:", pc.connectionState);
      
      if (pc.connectionState === "connected") {
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;
        setCallState((prev) => ({ ...prev, isReconnecting: false, isConnected: true }));
      } else if (pc.connectionState === "disconnected") {
        // Start reconnection attempt
        handleReconnect(pc);
      } else if (pc.connectionState === "failed") {
        // Max retries exhausted or connection failed
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.log("Max reconnect attempts reached, ending call");
          endCall();
        } else {
          handleReconnect(pc);
        }
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("ICE connection state:", pc.iceConnectionState);
      if (pc.iceConnectionState === "disconnected") {
        setCallState((prev) => ({ ...prev, isReconnecting: true }));
      }
    };

    peerConnectionRef.current = pc;
    return pc;
  }, [otherUserId]);

  const handleReconnect = useCallback(async (pc: RTCPeerConnection) => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log("Max reconnect attempts reached");
      endCall();
      return;
    }

    reconnectAttemptsRef.current += 1;
    console.log(`Reconnect attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`);
    
    setCallState((prev) => ({ ...prev, isReconnecting: true, isConnected: false }));

    // Haptic feedback for reconnection
    if ("vibrate" in navigator) {
      navigator.vibrate([100, 50, 100]);
    }

    // Wait before attempting reconnection
    reconnectTimeoutRef.current = setTimeout(async () => {
      try {
        // Restart ICE to attempt reconnection
        if (pc && pc.connectionState !== "closed") {
          console.log("Restarting ICE...");
          const offer = await pc.createOffer({ iceRestart: true });
          await pc.setLocalDescription(offer);
          
          if (channelRef.current) {
            channelRef.current.send({
              type: "broadcast",
              event: "offer",
              payload: {
                offer,
                callId: callState.callId,
                targetId: otherUserId,
                isReconnect: true,
              },
            });
          }
        }
      } catch (error) {
        console.error("Reconnection failed:", error);
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          endCall();
        }
      }
    }, 2000);
  }, [callState.callId, otherUserId]);

  const startCall = useCallback(async () => {
    if (!user || !channelRef.current) return;

    try {
      // Get local media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;
      setCallState((prev) => ({ ...prev, localStream: stream, isCalling: true }));

      // Create call record
      const { data: callData, error } = await supabase
        .from("video_calls")
        .insert({
          caller_id: user.id,
          callee_id: otherUserId,
          chat_id: chatId,
        })
        .select()
        .single();

      if (error) throw error;

      // Setup peer connection
      const pc = setupPeerConnection();
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      channelRef.current.send({
        type: "broadcast",
        event: "offer",
        payload: {
          offer,
          callId: callData.id,
          targetId: otherUserId,
        },
      });

      setCallState((prev) => ({ ...prev, callId: callData.id }));
    } catch (error) {
      console.error("Error starting call:", error);
      endCall();
    }
  }, [user, chatId, otherUserId, setupPeerConnection]);

  const answerCall = useCallback(async () => {
    if (!user || !channelRef.current || !callState.callId) return;

    try {
      // Get local media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;
      setCallState((prev) => ({ ...prev, localStream: stream, isIncoming: false }));

      // Update call status
      await supabase
        .from("video_calls")
        .update({ status: "active", started_at: new Date().toISOString() })
        .eq("id", callState.callId);

      // Setup peer connection
      const pc = setupPeerConnection();
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // Wait for offer
      const offerHandler = async ({ payload }: { payload: any }) => {
        if (payload.targetId === user.id && payload.offer) {
          await pc.setRemoteDescription(new RTCSessionDescription(payload.offer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          channelRef.current?.send({
            type: "broadcast",
            event: "answer",
            payload: {
              answer,
              targetId: otherUserId,
            },
          });
        }
      };

      channelRef.current.on("broadcast", { event: "offer" }, offerHandler);
    } catch (error) {
      console.error("Error answering call:", error);
      declineCall();
    }
  }, [user, callState.callId, otherUserId, setupPeerConnection]);

  const declineCall = useCallback(async () => {
    if (callState.callId) {
      await supabase
        .from("video_calls")
        .update({ status: "declined", ended_at: new Date().toISOString() })
        .eq("id", callState.callId);
    }

    if (channelRef.current) {
      channelRef.current.send({
        type: "broadcast",
        event: "end-call",
        payload: { targetId: otherUserId },
      });
    }

    setCallState({
      callId: null,
      isIncoming: false,
      isCalling: false,
      isConnected: false,
      isReconnecting: false,
      isScreenSharing: false,
      localStream: null,
      remoteStream: null,
    });
  }, [callState.callId, otherUserId]);

  const endCall = useCallback(async () => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    // Stop screen share stream
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Update call status
    if (callState.callId) {
      await supabase
        .from("video_calls")
        .update({ status: "ended", ended_at: new Date().toISOString() })
        .eq("id", callState.callId);
    }

    // Notify other user
    if (channelRef.current) {
      channelRef.current.send({
        type: "broadcast",
        event: "end-call",
        payload: { targetId: otherUserId },
      });
    }

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = 0;

    setCallState({
      callId: null,
      isIncoming: false,
      isCalling: false,
      isConnected: false,
      isReconnecting: false,
      isScreenSharing: false,
      localStream: null,
      remoteStream: null,
    });
  }, [callState.callId, otherUserId]);

  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
      }
    }
  }, []);

  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
      }
    }
  }, []);

  const toggleScreenShare = useCallback(async () => {
    if (!peerConnectionRef.current || !localStreamRef.current) return;

    const pc = peerConnectionRef.current;
    const isCurrentlySharing = callState.isScreenSharing;

    if (isCurrentlySharing) {
      // Stop screen sharing, switch back to camera
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track) => track.stop());
        screenStreamRef.current = null;
      }

      // Get the video sender and replace track with camera
      const videoSender = pc.getSenders().find((s) => s.track?.kind === "video");
      if (videoSender && localStreamRef.current) {
        const cameraTrack = localStreamRef.current.getVideoTracks()[0];
        if (cameraTrack) {
          await videoSender.replaceTrack(cameraTrack);
        }
      }

      setCallState((prev) => ({ ...prev, isScreenSharing: false }));
    } else {
      // Start screen sharing
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false,
        });
        screenStreamRef.current = screenStream;

        // Replace video track with screen share
        const videoSender = pc.getSenders().find((s) => s.track?.kind === "video");
        const screenTrack = screenStream.getVideoTracks()[0];

        if (videoSender && screenTrack) {
          await videoSender.replaceTrack(screenTrack);

          // Listen for when user stops sharing via browser UI
          screenTrack.onended = () => {
            toggleScreenShare();
          };
        }

        setCallState((prev) => ({ ...prev, isScreenSharing: true }));
      } catch (error) {
        console.error("Error starting screen share:", error);
      }
    }
  }, [callState.isScreenSharing]);

  return {
    callState,
    peerConnection: peerConnectionRef.current,
    startCall,
    answerCall,
    declineCall,
    endCall,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
  };
};
